/** @typedef {import("../../../api/types").Client} Client */
/** @typedef {import("../../../api/types").Order} Order */
/** @typedef {import("../../../api/types").ServiceItem} ServiceItem */
/** @typedef {import("../../../api/types").Product} Product */
/** @typedef {import("../../../api/types").DeviceType} DeviceType */
/** @typedef {import("./OrderItemsEditor").ItemRow} ItemRow */
/** @typedef {import("./OrderProductsEditor").ProductRow} ProductRow */
/** @typedef {import("./OrderDevicesEditor").DeviceRow} DeviceRow */
/** @typedef {import("./OrderDiscountEditor").Discount} Discount */
import React, {useEffect, useState} from "react";
import axios from "axios";
import {Link, useNavigate} from "react-router-dom";
import {
    Alert,
    Button,
    Card,
    CardBody,
    CardHeader,
    Container,
    Form,
    FormFeedback,
    FormGroup,
    Input,
    Spinner,
} from "reactstrap";
import OrderCreatedModal from "./OrderCreatedModal";
import OrderDevicesEditor from "./OrderDevicesEditor";
import OrderDiscountEditor from "./OrderDiscountEditor";
import InfoTip from "../../../shared/components/InfoTip";
import SearchSelect from "../../../shared/components/SearchSelect";
import Subtitle from "../../../shared/components/Subtitle";
import "./OrderForm.css";
import {TotalsCard} from "./OrderSummaries";
import OrderItemsEditor from "./OrderItemsEditor";
import OrderProductsEditor from "./OrderProductsEditor";
import {fetchAll} from "../../../api/fetchAll";
import {buildOrderPayload, catalogForOrder, orderToFormState} from "../utils/orderForm";
import {parseOrderErrors} from "../utils/orderErrors";
import {orderTotals} from "../utils/orderTotals";
import ListLink from "../../../shared/components/ListLink";
import {listTarget} from "../../../shared/utils/listMemory";
import IconLabel from "../../../shared/components/IconLabel";
import {
    CheckIcon,
    DeviceIcon,
    BoxIcon,
    ListIcon,
    NoteIcon,
    PercentIcon,
    PlusIcon,
    UserIcon,
    XIcon,
} from "../../../shared/components/icons";

const API = "/os/api/";
const NO_ERRORS = {general: [], fields: {}, items: {}, products: {}, devices: {}};

const EMPTY_STATE = {clientId: "", observations: "", items: [], products: [], devices: [], discount: null};

/**
 * The form for an order. Creating, it can start out filled with `cloneFrom`'s
 * values (the "cancel and clone" flow); with `editing` it changes that order
 * instead (only an open or sent one can be).
 * @param {{cloneFrom?: Order, editing?: Order}} props
 */
const OrderForm = ({cloneFrom, editing}) => {
    const navigate = useNavigate();
    // back to the orders list as it was, with its filters and scroll
    const backToOrders = listTarget("/orders");
    // The order just created, while its link modal is open.
    const [created, setCreated] = useState(/** @type {Order|null} */ (null));
    const [initial] = useState(() => {
        const source = editing ?? cloneFrom;
        return source ? orderToFormState(source) : EMPTY_STATE;
    });
    // Where leaving the form goes: the order being edited, or the list.
    const leaveTo = editing ? {to: `/orders/${editing.id}`, state: undefined} : backToOrders;

    // Options for the selects.
    const [clients, setClients] = useState(/** @type {Client[]} */ ([]));
    const [serviceItems, setServiceItems] = useState(/** @type {ServiceItem[]} */ ([]));
    const [productList, setProductList] = useState(/** @type {Product[]} */ ([]));
    const [deviceTypes, setDeviceTypes] = useState(/** @type {DeviceType[]} */ ([]));
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    // Names of cloned services and products that could not be carried over.
    const [droppedItems, setDroppedItems] = useState(/** @type {string[]} */ ([]));

    // Form state.
    const [clientId, setClientId] = useState(initial.clientId);
    const [items, setItems] = useState(/** @type {ItemRow[]} */ (initial.items));
    const [products, setProducts] = useState(/** @type {ProductRow[]} */ (initial.products));
    const [devices, setDevices] = useState(/** @type {DeviceRow[]} */ (initial.devices));
    const [discount, setDiscount] = useState(/** @type {Discount|null} */ (initial.discount));
    const [observations, setObservations] = useState(initial.observations);

    const [errors, setErrors] = useState(NO_ERRORS);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const controller = new AbortController();
        const {signal} = controller;

        Promise.all([
            fetchAll(`${API}clients/`, signal),
            fetchAll(`${API}service_items/`, signal),
            fetchAll(`${API}products/`, signal),
            fetchAll(`${API}device_types/`, signal),
        ])
            .then(([c, si, pr, dt]) => {
                // Inactive ones can't be put on new orders (an edited order keeps the ones it has).
                const services = catalogForOrder(si, editing?.order_items ?? [], (line) =>
                    String(line.service_item.id)
                );
                const goods = catalogForOrder(pr, editing?.order_products ?? [], (line) => String(line.product.id));
                setClients(c);
                setServiceItems(services.entries);
                setProductList(goods.entries);
                setDeviceTypes(dt);

                if (cloneFrom) {
                    setItems((rows) => rows.filter((row) => services.ids.has(row.serviceItemId)));
                    setProducts((rows) => rows.filter((row) => goods.ids.has(row.productId)));
                    setDroppedItems([
                        ...cloneFrom.order_items
                            .filter((line) => !services.ids.has(String(line.service_item.id)))
                            .map((line) => line.service_item.name),
                        ...(cloneFrom.order_products ?? [])
                            .filter((line) => !goods.ids.has(String(line.product.id)))
                            .map((line) => line.product.name),
                    ]);
                }
            })
            .catch((err) => {
                if (!axios.isCancel(err)) setLoadError(true);
            })
            .finally(() => {
                if (!signal.aborted) setLoading(false);
            });

        return () => controller.abort();
        // Runs once; `cloneFrom` never changes for a given form.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const currency = serviceItems[0]?.currency ?? productList[0]?.currency ?? (editing ?? cloneFrom)?.currency;
    const itemsById = new Map(serviceItems.map((item) => [String(item.id), item]));
    const productsById = new Map(productList.map((product) => [String(product.id), product]));

    const totals = orderTotals(
        [
            ...items
                .filter((row) => itemsById.has(row.serviceItemId) && Number(row.quantity) > 0)
                .map((row) => ({
                    baseValue: Number(itemsById.get(row.serviceItemId)?.base_value),
                    quantity: Number(row.quantity),
                })),
            ...products
                .filter((row) => productsById.has(row.productId) && Number(row.quantity) > 0)
                .map((row) => ({
                    baseValue: Number(productsById.get(row.productId)?.base_value),
                    quantity: Number(row.quantity),
                })),
        ],
        discount?.type ?? "",
        Number(discount?.value) || 0
    );

    const complete =
        clientId &&
        items.length + products.length > 0 &&
        items.every((row) => row.serviceItemId && Number(row.quantity) >= 1) &&
        products.every((row) => row.productId && Number(row.quantity) >= 1) &&
        devices.every((row) => row.description.trim());

    const handleSubmit = async (event) => {
        event.preventDefault();
        setErrors(NO_ERRORS);
        setSubmitting(true);
        try {
            const payload = buildOrderPayload({clientId, observations, items, products, devices, discount});
            if (editing) {
                await axios.put(`${API}service_orders/${editing.id}/`, payload);
                navigate(leaveTo.to, {replace: true});
            } else {
                const {data} = await axios.post(`${API}service_orders/`, payload);
                setCreated(data);
            }
        } catch (error) {
            setErrors(parseOrderErrors(error, {editing: Boolean(editing)}));
            setSubmitting(false);
            window.scrollTo({top: 0, behavior: "smooth"});
        }
    };

    return (
        <Container className="mt-4 mb-5" style={{maxWidth: 860}}>
            {editing ? (
                <Link to={leaveTo.to}>&larr; Ordem de serviço nº {editing.number}</Link>
            ) : (
                <ListLink basePath="/orders">&larr; Ordens de serviço</ListLink>
            )}
            <h3 className="mt-3 mb-1">
                {editing ? `Editar ordem de serviço nº ${editing.number}` : "Nova ordem de serviço"}
            </h3>
            <Subtitle>
                {editing
                    ? "Altere o que for preciso e salve. Os serviços e produtos mantêm o preço de quando entraram na ordem; os novos usam o preço atual do catálogo."
                    : "Para criar a ordem, escolha o cliente e adicione pelo menos um serviço ou produto. Equipamentos, desconto e observações são opcionais."}
            </Subtitle>

            {editing?.status === "Sent" && (
                <Alert color="info">
                    Esta ordem já foi enviada ao cliente. O que você salvar vale para quem abrir o link a partir de
                    agora.
                </Alert>
            )}

            {cloneFrom && (
                <Alert color="info">
                    Cópia da ordem de serviço nº {cloneFrom.number}. A ordem original foi cancelada. Os preços dos
                    serviços e produtos seguem o catálogo atual.
                </Alert>
            )}
            {droppedItems.length > 0 && (
                <Alert color="warning">
                    Serviços e produtos que não estão mais ativos não foram copiados: {droppedItems.join(", ")}.
                </Alert>
            )}

            {loading && (
                <div className="text-center">
                    <Spinner />
                </div>
            )}
            {loadError && (
                <Alert color="danger">
                    Não foi possível carregar clientes, serviços, produtos e tipos de equipamento.
                </Alert>
            )}
            {errors.general.map((message) => (
                <Alert color="danger" key={message}>
                    {message}
                </Alert>
            ))}

            {!loading && !loadError && (
                <Form onSubmit={handleSubmit} noValidate>
                    <div className="row g-3 mb-3">
                        <div className="col-12 col-md-6">
                            <Card className="h-100">
                                <CardHeader className="d-flex align-items-center gap-2">
                                    <IconLabel icon={<UserIcon />}>Cliente</IconLabel>
                                    <InfoTip>Digite para buscar por nome, telefone ou e-mail.</InfoTip>
                                </CardHeader>
                                <CardBody>
                                    <FormGroup className="mb-0">
                                        <SearchSelect
                                            ariaLabel="Cliente"
                                            placeholder="Buscar cliente…"
                                            emptyMessage="Nenhum cliente encontrado."
                                            invalid={Boolean(errors.fields.client_id)}
                                            value={clientId}
                                            onChange={setClientId}
                                            options={clients.map((c) => ({
                                                value: String(c.id),
                                                label: `${c.name} — ${c.phone}`,
                                                search: c.email ?? "",
                                            }))}
                                        />
                                        <FormFeedback className={errors.fields.client_id ? "d-block" : undefined}>
                                            {errors.fields.client_id}
                                        </FormFeedback>
                                    </FormGroup>
                                </CardBody>
                            </Card>
                        </div>
                        <div className="col-12 col-md-6">
                            <Card className="h-100 card-text-field">
                                <CardHeader>
                                    <IconLabel icon={<NoteIcon />}>Observações</IconLabel>
                                </CardHeader>
                                <CardBody>
                                    <Input
                                        type="textarea"
                                        rows={2}
                                        // No border of its own: the card is the box.
                                        className="border-0 p-0 shadow-none"
                                        placeholder="Escreva uma observação…"
                                        aria-label="Observações"
                                        value={observations}
                                        onChange={(e) => setObservations(e.target.value)}
                                    />
                                </CardBody>
                            </Card>
                        </div>
                    </div>

                    <Card className="mb-3">
                        <CardHeader>
                            <IconLabel icon={<DeviceIcon />}>Equipamentos do cliente</IconLabel>
                        </CardHeader>
                        <CardBody>
                            <OrderDevicesEditor
                                rows={devices}
                                deviceTypes={deviceTypes}
                                errors={errors.devices}
                                onChange={setDevices}
                            />
                        </CardBody>
                    </Card>

                    <Card className="mb-3">
                        <CardHeader>
                            <IconLabel icon={<ListIcon />}>Serviços</IconLabel>
                        </CardHeader>
                        <CardBody>
                            <OrderItemsEditor
                                rows={items}
                                serviceItems={serviceItems}
                                currency={currency}
                                errors={errors.items}
                                onChange={setItems}
                            />
                        </CardBody>
                    </Card>

                    <Card className="mb-3">
                        <CardHeader>
                            <IconLabel icon={<BoxIcon />}>Produtos</IconLabel>
                        </CardHeader>
                        <CardBody>
                            <OrderProductsEditor
                                rows={products}
                                products={productList}
                                currency={currency}
                                errors={errors.products}
                                onChange={setProducts}
                            />
                        </CardBody>
                    </Card>

                    <Card className="mb-3">
                        <CardHeader>
                            <IconLabel icon={<PercentIcon />}>Desconto</IconLabel>
                        </CardHeader>
                        <CardBody>
                            <OrderDiscountEditor
                                discount={discount}
                                amount={totals.discount}
                                currency={currency}
                                errors={{
                                    ...(errors.fields.discount_type && {type: errors.fields.discount_type}),
                                    ...(errors.fields.discount_value && {value: errors.fields.discount_value}),
                                }}
                                onChange={setDiscount}
                            />
                        </CardBody>
                    </Card>

                    <TotalsCard {...totals} currency={currency} totalLabel="Total estimado" />

                    {/* Right-aligned, cancel then the main action: same as the modals' footers. */}
                    <div className="d-flex justify-content-end gap-2">
                        <Button
                            color="secondary"
                            outline
                            type="button"
                            disabled={submitting}
                            onClick={() => navigate(leaveTo.to, {state: leaveTo.state})}
                        >
                            <IconLabel icon={<XIcon />}>Cancelar</IconLabel>
                        </Button>
                        <Button color="primary" type="submit" disabled={submitting || !complete}>
                            {submitting ? (
                                <Spinner size="sm" />
                            ) : editing ? (
                                <IconLabel icon={<CheckIcon />}>Salvar alterações</IconLabel>
                            ) : (
                                <IconLabel icon={<PlusIcon />}>Criar ordem de serviço</IconLabel>
                            )}
                        </Button>
                    </div>
                </Form>
            )}
            {created && (
                <OrderCreatedModal order={created} onDone={() => navigate(`/orders/${created.id}`, {replace: true})} />
            )}
        </Container>
    );
};

export default OrderForm;
